import React from 'react';
import { Stack, Box } from '@mui/material';
import { InputSelect, InputText, Text } from '@innovyze/stylovyze';
import { WEAVE_FONT } from '@Utils/fonts';
import { pacpFieldMappings, wsaFieldMappings } from '@Types/inspections.types';
import { useFormikContext } from 'formik';

interface Props {
	inspectionStandard?: string;
}

interface ConditionScoreFormValues {
	conditionScoreKey: string;
	conditionScoreOperator: string;
	conditionScoreValue: number;
	conditionScoreValueTo: number;
}

// Define operators for condition scores
const conditionScoreOperators = {
	EQUALS: { display: 'Equals', valueType: 'number' },
	DOES_NOT_EQUAL: { display: 'Does Not Equal', valueType: 'number' },
	GREATER_THAN: { display: 'Greater Than', valueType: 'number' },
	LESS_THAN: { display: 'Less Than', valueType: 'number' },
	BETWEEN: { display: 'Between', valueType: 'number' },
};

export const ConditionScoreFilter = ({ inspectionStandard = 'PACP' }: Props) => {
	const { values, setFieldValue, errors, touched, submitCount } = useFormikContext<ConditionScoreFormValues>();

	const fieldMappings = inspectionStandard === 'PACP' ? pacpFieldMappings : wsaFieldMappings;

	const conditionScoreOptions = Object.values(fieldMappings).map(field => ({
		key: field.key,
		value: field.key,
		label: field.display_name,
	}));

	const operatorOptions = Object.entries(conditionScoreOperators).map(([key, op]) => ({
		key,
		value: key,
		label: op.display,
	}));

	const showError = (field: keyof ConditionScoreFormValues) =>
		(touched?.[field] || (submitCount ?? 0) > 0) && !!errors?.[field];

	const getHelperText = (field: keyof ConditionScoreFormValues) => {
		const shouldShow = touched?.[field] || (submitCount ?? 0) > 0;
		return shouldShow && errors?.[field] ? (errors[field] as string) : undefined;
	};

	const isBetweenOperator = values.conditionScoreOperator === 'BETWEEN';

	return (
		<Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 2 }}>
			<Box sx={{ width: '160px', flexShrink: 0 }}>
				<Text variant="text-medium" sx={{ fontFamily: WEAVE_FONT }}>
					Condition Score
				</Text>
			</Box>

			<Box sx={{ flex: '1', minWidth: '120px', maxWidth: '300px' }}>
				<InputSelect
					menuItems={conditionScoreOptions}
					fieldAttrs={{ name: 'conditionScoreKey' }}
					selectProps={{
						label: 'Condition Score',
						fullWidth: true,
						required: true,
						error: showError('conditionScoreKey'),
					}}
					helperText={getHelperText('conditionScoreKey')}
					onChange={e => {
						setFieldValue('conditionScoreKey', e.target.value);
						setFieldValue('conditionScoreOperator', '');
					}}
				/>
			</Box>

			<Box sx={{ flex: '1', minWidth: '120px', maxWidth: '200px' }}>
				<InputSelect
					menuItems={operatorOptions}
					fieldAttrs={{ name: 'conditionScoreOperator' }}
					selectProps={{
						label: 'Operator',
						fullWidth: true,
						required: true,
						error: showError('conditionScoreOperator'),
					}}
					helperText={getHelperText('conditionScoreOperator')}
					onChange={e => {
						setFieldValue('conditionScoreOperator', e.target.value);
						setFieldValue('conditionScoreValue', '');
						setFieldValue('conditionScoreValueTo', '');
					}}
				/>
			</Box>

			<Box sx={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
				{isBetweenOperator ? (
					<Stack direction="row" alignItems="flex-start" spacing={0.5}>
						<InputText
							fieldAttrs={{ name: 'conditionScoreValue' }}
							textField={{
								label: 'From',
								fullWidth: true,
								required: true,
								type: 'number',
								error: showError('conditionScoreValue'),
								helperText: getHelperText('conditionScoreValue'),
							}}
						/>
						<Box sx={{ display: 'flex', alignItems: 'center', height: '56px', px: 0.5 }}>
							<Text variant="text-medium">to</Text>
						</Box>
						<InputText
							fieldAttrs={{ name: 'conditionScoreValueTo' }}
							textField={{
								label: 'To',
								fullWidth: true,
								required: true,
								type: 'number',
								error: showError('conditionScoreValueTo'),
								helperText: getHelperText('conditionScoreValueTo'),
							}}
						/>
					</Stack>
				) : (
					<InputText
						fieldAttrs={{ name: 'conditionScoreValue' }}
						textField={{
							label: 'Value',
							fullWidth: true,
							required: true,
							type: 'number',
							error: showError('conditionScoreValue'),
							helperText: getHelperText('conditionScoreValue'),
						}}
					/>
				)}
			</Box>
		</Stack>
	);
};
